import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { FadeUp, FadeIn } from "../components/Animations";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["All", "Residential", "Remodel", "New Build"];

function PortfolioGrid() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then((res) => setProjects(res.data))
      .catch(() => {});
  }, []);

  const filtered =
    filter === "All"
      ? projects
      : projects.filter((p) => p.category === filter);

  return (
    <div data-testid="portfolio-page" className="pt-20">
      {/* Header */}
      <section className="py-20 md:py-28 px-6 md:px-16 max-w-[1400px] mx-auto">
        <FadeUp>
          <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-8">
            Portfolio
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-onyx leading-[1.05] max-w-3xl">
            Selected Work
          </h1>
        </FadeUp>
      </section>

      {/* Filters */}
      <section className="px-6 md:px-16 max-w-[1400px] mx-auto mb-16">
        <FadeUp delay={0.2}>
          <div data-testid="portfolio-filters" className="flex flex-wrap gap-6">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase().replace(" ", "-")}`}
                onClick={() => setFilter(cat)}
                className={`text-sm tracking-[0.15em] uppercase font-light pb-1 transition-all duration-300 ${
                  filter === cat
                    ? "text-onyx border-b border-onyx"
                    : "text-taupe hover:text-onyx"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* Grid */}
      <section className="px-6 md:px-16 max-w-[1400px] mx-auto pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filtered.map((project, i) => (
            <FadeUp key={project.id} delay={i * 0.08}>
              <Link
                to={`/portfolio/${project.id}`}
                data-testid={`project-card-${i}`}
                className="group block"
              >
                <div
                  className={`img-scale w-full aspect-[4/3]`}
                >
                  <img
                    src={project.thumbnail}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-4">
                  <h3 className="font-serif text-xl font-light text-onyx group-hover:text-taupe transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-xs tracking-[0.15em] uppercase text-taupe mt-1">
                    {project.location} — {project.category}
                  </p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>
    </div>
  );
}

function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/projects/${id}`)
      .then((res) => setProject(res.data))
      .catch(() => navigate("/portfolio"));
  }, [id, navigate]);

  if (!project) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border border-onyx border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="project-detail" className="pt-20">
      {/* Back */}
      <section className="py-12 px-6 md:px-16 max-w-[1400px] mx-auto">
        <button
          data-testid="project-back-btn"
          onClick={() => navigate("/portfolio")}
          className="inline-flex items-center gap-2 text-sm tracking-[0.15em] uppercase font-light text-taupe hover:text-onyx transition-colors"
        >
          <ArrowLeft size={16} /> Back to Portfolio
        </button>
      </section>

      {/* Title */}
      <section className="px-6 md:px-16 max-w-[1400px] mx-auto pb-12">
        <FadeUp>
          <h1 className="font-serif text-4xl md:text-6xl font-light text-onyx">
            {project.title}
          </h1>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="flex flex-wrap gap-6 mt-4">
            <span className="text-xs tracking-[0.2em] uppercase text-taupe">
              {project.location}
            </span>
            <span className="text-xs tracking-[0.2em] uppercase text-taupe">
              {project.category}
            </span>
            {project.year && (
              <span className="text-xs tracking-[0.2em] uppercase text-taupe">
                {project.year}
              </span>
            )}
          </div>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="text-base font-light text-onyx/70 leading-[1.85] max-w-2xl mt-8">
            {project.description}
          </p>
        </FadeUp>
      </section>

      {/* Images */}
      <section className="px-6 md:px-16 max-w-[1400px] mx-auto pb-24 md:pb-32">
        <div className="grid grid-cols-1 gap-4">
          {project.images.map((img, i) => (
            <FadeUp key={i} delay={i * 0.1}>
              <div
                className="cursor-pointer img-scale"
                onClick={() => setLightbox(i)}
              >
                <img
                  src={img.url}
                  alt={img.caption || project.title}
                  className="w-full h-auto object-cover"
                />
                {img.caption && (
                  <p className="text-xs tracking-[0.15em] uppercase text-taupe mt-3">
                    {img.caption}
                  </p>
                )}
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 bg-onyx/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-6 right-6 text-bone hover:text-taupe"
            onClick={() => setLightbox(null)}
          >
            <X size={28} />
          </button>
          <button
            className="absolute left-6 top-1/2 -translate-y-1/2 text-bone hover:text-taupe"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(
                (lightbox - 1 + project.images.length) % project.images.length
              );
            }}
          >
            <ArrowLeft size={28} />
          </button>
          <button
            className="absolute right-6 top-1/2 -translate-y-1/2 text-bone hover:text-taupe"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((lightbox + 1) % project.images.length);
            }}
          >
            <ArrowRight size={28} />
          </button>
          <img
            src={project.images[lightbox].url}
            alt=""
            className="max-w-full max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* CTA */}
      <section className="bg-onyx py-20 px-6 md:px-16 text-center">
        <FadeUp>
          <h2 className="font-serif text-3xl md:text-5xl font-light text-bone mb-8">
            Inspired by this project?
          </h2>
        </FadeUp>
        <FadeUp delay={0.15}>
          <Link
            to="/contact"
            data-testid="project-contact-cta"
            className="inline-block bg-bone text-onyx px-10 py-4 text-sm tracking-[0.2em] uppercase font-light hover:bg-taupe hover:text-bone transition-colors duration-500"
          >
            Start Your Project
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}

export default function PortfolioPage() {
  const { id } = useParams();
  return id ? <ProjectDetail /> : <PortfolioGrid />;
}
